import { ref, reactive } from 'vue';
import { getSppTable, autoCompleteSpeciesName } from '@/services/data/biobrowser-data.js';
import { wetlandOption, osrOption, defaultColumns, wetlandColumn, osrColumn, countPerPage, getGroupIdByName, cmsProfileColumn, cmsProfileOption } from '@/config/spp-list-config.js';
const appState = reactive({
  inFilterTerm: '', /* this is for holding user input, before they click enter or search button, just to save api calls when users enter partial words */
  filterTerm: '', /* when user click search, move inFilterTerm to filterTerm and start to query */
  searchTerms: { cname: '', sname: '', summary: '', wetland: '', osr: '' },
  // auto complete
  autoCompleteSppList: [],
  isAutoCompleteLoading: false,

  /* table */
  // page,
  list: [],
  columns: reactive(defaultColumns),
  order: reactive([{ column: 0, dir: 'asc' }]),

  filteredRecordNo: 0,
  totalRecordNo: -1 // has to be -1 for initial state

});

export function useAppState () {
  return appState;
}

function app () {
  const state = useAppState();
  const childLoadingRef = ref(null);
  // Refs can be updated async
  state.groupId = +(new URL(location.href).searchParams.get('groupId') || 1);
  const hasOSRData = [1, 7].includes(state.groupId); // bird and mammal only
  const hasWetlandColumn = (state.groupId === getGroupIdByName('Vascular Plants'));
  const hasOSRColumn = (state.groupId === getGroupIdByName('Birds') || state.groupId === getGroupIdByName('Mammals'));
  const hasCMSProfileColumn = (state.groupId === getGroupIdByName('Vascular Plants')) || (state.groupId === getGroupIdByName('Lichens'));
  let year = 2021;
  let _searchTimerId;
  // Init loads anything needed at startup
  const init = async (inYear) => {
    year = inYear;
    if (hasWetlandColumn) {
      state.columns.push(wetlandColumn);
    }
    if (hasOSRColumn) {
      state.columns.push(osrColumn);
    }
    if (hasCMSProfileColumn) {
      state.columns.push(cmsProfileColumn);
    }
  };

  const clearAllFilter = () => {
    Object.keys(state.searchTerms).forEach(x => { state.searchTerms[x] = ''; });
    state.inFilterTerm = '';
    state.filterTerm = '';
    filterTable();
  }

  /* manual search */
  const manualSearch = () => {
    state.filterTerm = state.inFilterTerm;
    filterTable();
    state.autoCompleteSppList = [];
  }

  const searchForAutoCompleteDebounced = (key) => {
    if (key.length < 3) {
      state.autoCompleteSppList = [];
      return;
    }
    clearTimeout(_searchTimerId);
    _searchTimerId = setTimeout(() => {
      searchForAutoComplete(key)
    }, 800) /* 500ms throttle */
  };
  /* auto complete filter box */
  const searchForAutoComplete = async (key) => {
    state.isAutoCompleteLoading = true;
    state.autoCompleteSppList = await autoCompleteSpeciesName(state.groupId, year, key);
    state.isAutoCompleteLoading = false;
  };

  const pickAutoCompleteItem = (item) => {
    const label = ([1, 9].includes(state.groupId) ? item.common || item.commonName || item.scientific || item.scientificName : item.scientific || item.scientificName)
    state.inFilterTerm = label;
    state.filterTerm = label;
    filterTable();
    state.autoCompleteSppList = [];
  }

  const dataConfig = reactive({
    columns: state.columns,
    order: state.order,
    start: 1, // start page from 1
    length: countPerPage,
    groupId: state.groupId,
  });
  const tableRedrawIdentifier = ref(1);
  const isTableLoaded = ref(false);
  const filterTable = () => {
    state.filteredRecordNo = -1;
    state.list = [];
    dataConfig.start = 1;
    dataConfig.length = countPerPage;
    dataConfig.filter = state.filterTerm;
    state.columns.forEach(c => { c.searchValue = '' }); // clear other search
    state.columns[0].searchValue = state.searchTerms.cname;
    state.columns[1].searchValue = state.searchTerms.sname;
    state.columns[2].searchValue = state.searchTerms.summary;
    let nextColumnIndex = 3;
    if (hasOSRData) { // birds and mammals
      state.columns[nextColumnIndex].searchValue = osrOption.find(x => x.label === state.searchTerms?.osr)?.value;
      nextColumnIndex += 1;
    } else if (hasWetlandColumn) {
      state.columns[nextColumnIndex].searchValue = wetlandOption.find(x => x.label === state.searchTerms?.wetland)?.value;
      nextColumnIndex += 1;
    }
    if (hasCMSProfileColumn) {
      state.columns[nextColumnIndex].searchValue = cmsProfileOption.find(x => x.label === state.searchTerms?.cmsProfile)?.value;
    }
    tableRedrawIdentifier.value += 1;
    isTableLoaded.value = false;
  };

  const infiniteHandler = async ($state) => { /* this will auto triggered when div initialized or reset. so no need to manual load data */
    const data = await getSppTable(year, dataConfig);
    if (state.totalRecordNo === -1) {
      state.totalRecordNo = data?.recordsTotal || 0;
    }
    state.filteredRecordNo = data?.recordsTotal || 0;
    isTableLoaded.value = true;
    if (data?.data?.length) {
      dataConfig.start += 1;
      state.list.push(...data.data);

      // $state && $state.loaded();
      if (data.data.length < countPerPage) {
        $state && $state.complete();
      } else {
        $state && $state.loaded();
      }
    } else {
      $state && $state.complete();
    }
  };

  const sortColumn = (columnIndex) => {
    if (columnIndex === state.order[0].column) { // same column, so just swtich order, ignore pass in dir
      state.order[0].dir = (state.order[0].dir === 'asc' ? 'desc' : 'asc');
    } else {
      state.order[0].column = columnIndex;
      state.order[0].dir = (columnIndex === 3 ? 'desc' : 'asc'); // has wetland (#3) sort by reverse order
    }

    filterTable();
  };

  return {
    state,
    init,
    hasOSRData,
    clearAllFilter,

    manualSearch,

    searchForAutoCompleteDebounced,
    pickAutoCompleteItem,

    infiniteHandler,
    filterTable,

    sortColumn,

    childLoadingRef,
    hasWetlandColumn,
    hasOSRColumn,
    hasCMSProfileColumn,
    tableRedrawIdentifier,
    isTableLoaded,
  };
}
const appSingleton = app();
export function sppSearchState () {
  // NOTE: Any setup hooks must be called here.
  // This gets called from every setup() that uses the app.
  return appSingleton;
}
